/**
 * 🎨 GRID FLOATING TOOLBAR 2025
 * 
 * Floating Toolbar für Grid Layout Elements
 * Ermöglicht Inline-Editing von Grid-Properties
 * 
 * Features:
 * - Gap (Desktop/Mobile)
 * - Column Template (equal, 1-2, 2-1, etc.)
 * - Margins
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import CellLayoutSettings from './CellLayoutSettings';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import './GridFloatingToolbar.css';

function GridFloatingToolbar({
    isEditMode,
    isVisible,
    gridElement,
    onStyleChange,
    onClose,
    gridRef,
    isMobileLayout = false,
    colors = {} // ✅ Brand Colors vom Space
}) {
    const [showToolbar, setShowToolbar] = useState(false);
    const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
    const toolbarRef = useRef(null);
    
    // 🎯 Dragging State
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    
    // Device Toggle
    const [device, setDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    
    // Expandable Sections State
    const [expandedSection, setExpandedSection] = useState(null);
    
    // ✅ KEIN lokaler optimistic State mehr!
    // → gridElement IST bereits optimistic (merged in LayoutGridElement)
    // → Slider Values kommen direkt aus gridElement
    // → onChange ruft direkt onStyleChange auf
    // → KEIN Toolbar Re-Render = SMOOTH wie TipTap! 🚀
    
    // ⚡ Style Change Handler - DIREKT an Parent (wie Divider/Image/Button Toolbar!)
    const handleStyleChange = (updates) => {
        // console.log('🔄 [Grid Toolbar] Style change (instant):', updates);
        
        // ⚡ DIREKT an Parent - KEIN lokaler State!
        // → LayoutGridElement updated localOptimisticStyles (instant)
        // → LayoutGridElement's useEffect macht DOM-Manipulation
        // → gridElement prop wird re-merged mit optimistic values
        // → Slider Value updated instant (gridElement ist bereits merged!)
        // → KEIN Toolbar Re-Render Delay = SMOOTH!
        onStyleChange(updates);
    };
    
    // ✅ Custom Color Slots (shared mit TipTap via LocalStorage) - 5 Slots für User-Farben
    const [customColorSlots, setCustomColorSlots] = useState(() => {
        try {
            const stored = localStorage.getItem('tiptap-custom-color-slots');
            return stored ? JSON.parse(stored) : ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        } catch (error) {
            return ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        }
    });
    
    // ✅ Save Custom Colors to LocalStorage (nur wenn sich ändern)
    const saveCustomColor = (index, color) => {
        const newSlots = [...customColorSlots];
        newSlots[index] = color;
        setCustomColorSlots(newSlots);
        try {
            localStorage.setItem('tiptap-custom-color-slots', JSON.stringify(newSlots));
        } catch (error) {
            console.error('Failed to save custom color slots:', error);
        }
    };
    
    // Sync device with isMobileLayout
    useEffect(() => {
        setDevice(isMobileLayout ? 'mobile' : 'desktop');
    }, [isMobileLayout]);
    
    // 🎯 Drag Handlers - MUSS VOR early return stehen!
    const handleMouseDown = (e) => {
        // Verhindere Drag bei Button-Klicks und Sections
        if (e.target.closest('.grid-icon-btn') || 
            e.target.closest('.grid-device-toggle') ||
            e.target.closest('.grid-expanded-section')) {
            return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        // ✅ Bounds checking - Toolbar bleibt im Viewport
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 320);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 200);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true);
    };
    
    // ✅ Drag Event Listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);
    
    // Show Toolbar when visible
    useEffect(() => {
        if (isEditMode && isVisible) {
            setShowToolbar(true);
            updateToolbarPosition();
        } else {
            setShowToolbar(false);
            setExpandedSection(null);
        }
    }, [isEditMode, isVisible]);
    
    // Update Toolbar Position
    const updateToolbarPosition = () => {
        if (expandedSection || isManuallyPositioned) {
            setShowToolbar(true);
            return;
        }
        
        const top = 80;
        const left = window.innerWidth / 2;
        setToolbarPosition({ top, left });
    };
    
    // Update Position on Resize
    useEffect(() => {
        if (!showToolbar) return;
        
        const handleResize = () => {
            if (!expandedSection) {
                updateToolbarPosition();
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [showToolbar, expandedSection]);
    
    // Handle Outside Click
    useEffect(() => {
        if (!showToolbar) return;
        
        const handleClickOutside = (event) => {
            const clickedInToolbar = toolbarRef.current && toolbarRef.current.contains(event.target);
            const clickedInGrid = gridRef && gridRef.current && gridRef.current.contains(event.target);
            
            if (!clickedInToolbar && !clickedInGrid) {
                if (onClose) onClose();
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showToolbar, onClose]);
    
    if (!isEditMode || !isVisible || !showToolbar) {
        return null;
    }
    
    // ⚡ DIREKT aus gridElement - KEIN lokaler State!
    // → Wie Divider/Image Toolbar: Value kommt immer von gridElement
    // → LayoutGridElement macht optimistic updates in seinem localOptimisticStyles
    // → gridElement wird debounced updated, localOptimisticStyles macht DOM instant
    const activeGap = device === 'mobile'
        ? (gridElement?.gapMobile ?? gridElement?.gap ?? 20)
        : (gridElement?.gap ?? 20);
    
    const activeMarginTop = device === 'mobile'
        ? (gridElement?.marginTopMobile ?? gridElement?.marginTop ?? 0)
        : (gridElement?.marginTop ?? 0);
    
    const activeMarginBottom = device === 'mobile'
        ? (gridElement?.marginBottomMobile ?? gridElement?.marginBottom ?? 0)
        : (gridElement?.marginBottom ?? 0);
    
    const activeMarginLeft = device === 'mobile'
        ? (gridElement?.marginLeftMobile ?? gridElement?.marginLeft ?? 0)
        : (gridElement?.marginLeft ?? 0);
    
    const activeMarginRight = device === 'mobile'
        ? (gridElement?.marginRightMobile ?? gridElement?.marginRight ?? 0)
        : (gridElement?.marginRight ?? 0);
    
    // ✨ INTELLIGENTE COLUMN TEMPLATES - Abhängig von Grid Direction & Columns
    const getColumnTemplates = (gridDirection, columns) => {
        // Aktuelle Grid Direction (responsive)
        const currentDirection = device === 'mobile'
            ? (gridElement?.gridDirectionMobile ?? gridElement?.gridDirection ?? 'horizontal')
            : (gridElement?.gridDirection ?? 'horizontal');
        
        // Aktuelle Spaltenanzahl
        const columnCount = gridElement?.columns || 2;
        
        // ✅ HORIZONTAL GRID (nebeneinander) - Column Width Templates
        if (currentDirection === 'horizontal') {
            if (columnCount === 2) {
                return [
                    { value: 'equal', label: 'Equal', icon: '▯▯', description: '50% / 50%', preview: '1 : 1' },
                    { value: '1-2', label: 'Small-Big', icon: '▯▯▯', description: 'Small left / Big right', preview: '1 : 2' },
                    { value: '2-1', label: 'Big-Small', icon: '▯▯▯', description: 'Big left / Small right', preview: '2 : 1' },
                    { value: '1-3', label: 'Tiny-Big', icon: '▯▯▯▯', description: 'Tiny left / Big right', preview: '1 : 3' },
                    { value: '3-1', label: 'Big-Tiny', icon: '▯▯▯▯', description: 'Big left / Tiny right', preview: '3 : 1' },
                    { value: 'auto-rest', label: 'Auto-Rest', icon: '▯▯▯', description: 'Left auto-fits / Right fills', preview: 'Auto | Rest' },
                    { value: 'fit-rest', label: 'Fit-Rest', icon: '▯▯▯', description: 'Left fits content (max 300px) / Right fills', preview: 'Fit | Rest' },
                    { value: 'auto-auto', label: 'Auto-Auto', icon: '▯▯', description: 'Both auto-fit content', preview: 'Auto | Auto' }
                ];
            } else if (columnCount === 3) {
                return [
                    { value: '1-1-1', label: 'Equal', icon: '▯▯▯', description: '33% / 33% / 33%', preview: '1:1:1' },
                    { value: '1-2-1', label: 'Side-Big-Side', icon: '▯▯▯▯', description: 'Small / Big / Small', preview: '1:2:1' },
                    { value: '2-1-1', label: 'Big-Small-Small', icon: '▯▯▯▯', description: 'Big / Small / Small', preview: '2:1:1' },
                    { value: 'auto-auto-rest', label: 'Auto-Auto-Rest', icon: '▯▯▯', description: 'Auto / Auto / Rest', preview: 'Auto|Auto|Rest' }
                ];
            } else if (columnCount === 4) {
                return [
                    { value: 'equal', label: 'Equal', icon: '▯▯▯▯', description: '25% each', preview: '1:1:1:1' },
                    { value: 'auto-auto-auto-auto', label: 'All Auto', icon: '▯▯▯▯', description: 'All auto-fit', preview: 'Auto' }
                ];
            }
        }
        
        // ✅ VERTICAL GRID (untereinander) - Row Height Templates
        else if (currentDirection === 'vertical') {
            if (columnCount === 2) {
                return [
                    { value: 'auto', label: 'Auto', icon: '☰', description: 'Both rows auto-height', preview: 'Auto', isVertical: true },
                    { value: 'equal', label: 'Equal', icon: '▬▬', description: '50% / 50%', preview: '1:1', isVertical: true },
                    { value: '1-2', label: 'Small-Big', icon: '▬▬▬', description: 'Small top / Big bottom', preview: '1:2', isVertical: true },
                    { value: '2-1', label: 'Big-Small', icon: '▬▬▬', description: 'Big top / Small bottom', preview: '2:1', isVertical: true },
                    { value: 'fit-rest', label: 'Fit-Rest', icon: '▬▬▬', description: 'Top fits / Bottom fills', preview: 'Fit|Rest', isVertical: true }
                ];
            } else if (columnCount === 3) {
                return [
                    { value: 'auto', label: 'Auto', icon: '☰', description: 'All rows auto-height', preview: 'Auto', isVertical: true },
                    { value: '1-1-1', label: 'Equal', icon: '▬▬▬', description: '33% each', preview: '1:1:1', isVertical: true },
                    { value: '1-2-1', label: 'S-B-S', icon: '▬▬▬', description: 'Small / Big / Small', preview: '1:2:1', isVertical: true }
                ];
            } else if (columnCount === 4) {
                return [
                    { value: 'auto', label: 'Auto', icon: '☰', description: 'All rows auto-height', preview: 'Auto', isVertical: true },
                    { value: 'equal', label: 'Equal', icon: '▬▬▬', description: '25% each', preview: '1:1:1:1', isVertical: true }
                ];
            }
        }
        
        // Fallback: Equal
        return [{ value: 'equal', label: 'Equal', icon: '▯▯', description: 'Equal spacing', preview: '1:1' }];
    };
    
    // ✅ Dynamische Templates basierend auf aktuellem Grid Direction
    const columnTemplates = getColumnTemplates(
        device === 'mobile' 
            ? (gridElement?.gridDirectionMobile ?? gridElement?.gridDirection ?? 'horizontal')
            : (gridElement?.gridDirection ?? 'horizontal'),
        gridElement?.columns || 2
    );
    
    const toolbarContent = (
        <div 
            ref={toolbarRef}
            className={`grid-floating-toolbar-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                position: 'fixed',
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 999999,
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none'
            }}
            onMouseDown={handleMouseDown}
        >
            <div className="grid-toolbar">
                {/* ✨ ICON TOOLBAR - Quick Access mit Drag + Close */}
                <div className="grid-icon-toolbar">
                    {/* Drag Handle am Anfang */}
                    <div className="grid-toolbar-drag">
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    <button
                        className={`grid-icon-btn ${expandedSection === 'layout' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'layout' ? null : 'layout')}
                        title="Layout"
                    >
                        <i className="fas fa-th-large"></i>
                    </button>
                    <button
                        className={`grid-icon-btn ${expandedSection === 'spacing' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'spacing' ? null : 'spacing')}
                        title="Spacing"
                    >
                        <i className="fas fa-arrows-alt-v"></i>
                    </button>
                    <button
                        className={`grid-icon-btn ${expandedSection === 'borderRadius' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'borderRadius' ? null : 'borderRadius')}
                        title="Border Radius"
                    >
                        <i className="fas fa-circle"></i>
                    </button>
                    <button
                        className={`grid-icon-btn ${expandedSection === 'border' ? 'active' : ''} ${gridElement?.borderEnabled ? 'has-feature' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'border' ? null : 'border')}
                        title="Border"
                    >
                        <i className="fas fa-border-style"></i>
                    </button>
                    <button
                        className={`grid-icon-btn ${expandedSection === 'background' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'background' ? null : 'background')}
                        title="Background"
                    >
                        <i className="fas fa-fill-drip"></i>
                    </button>
                    
                    <div className="grid-icon-divider"></div>
                    
                    {/* ✨ CELL LAYOUT */}
                    <button
                        className={`grid-icon-btn ${expandedSection === 'cellLayout' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'cellLayout' ? null : 'cellLayout')}
                        title="Cell Layout & Alignment"
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </button>
                    
                    {/* ✨ CELL STYLING */}
                    <button
                        className={`grid-icon-btn ${expandedSection === 'cellStyling' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'cellStyling' ? null : 'cellStyling')}
                        title="Cell Styling"
                    >
                        <i className="fas fa-paint-brush"></i>
                    </button>
                </div>
            
            {/* Main Controls */}
            <div className="grid-toolbar-controls">
                
                {/* ✨ LAYOUT SECTION */}
                {expandedSection === 'layout' && (
                    <div className="grid-expanded-section">
                        {/* Device Toggle */}
                        <div className="grid-device-toggle">
                            <button
                                className={device === 'desktop' ? 'active' : ''}
                                onClick={() => setDevice('desktop')}
                                title="Desktop"
                            >
                                <i className="fas fa-desktop"></i>
                                <span>Desktop</span>
                            </button>
                            <button
                                className={device === 'mobile' ? 'active' : ''}
                                onClick={() => setDevice('mobile')}
                                title="Mobile"
                            >
                                <i className="fas fa-mobile-alt"></i>
                                <span>Mobile</span>
                            </button>
                        </div>
                        
                        {/* Grid Direction - Desktop/Mobile separate */}
                        <div className="grid-control-group-full">
                            <label className="grid-control-label">Grid Direction</label>
                            <div className="grid-direction-buttons">
                                <button
                                    className={`grid-direction-btn ${
                                        (() => {
                                            if (device === 'mobile') {
                                                const mobileDir = gridElement?.gridDirectionMobile ?? (gridElement?.gridDirection || 'horizontal');
                                                return mobileDir === 'horizontal' || !mobileDir;
                                            } else {
                                                const desktopDir = gridElement?.gridDirection || 'horizontal';
                                                return desktopDir === 'horizontal' || !desktopDir;
                                            }
                                        })()
                                    ? 'active' : ''}`}
                                    onClick={() => {
                                        if (device === 'mobile') {
                                            handleStyleChange({ gridDirectionMobile: 'horizontal' });
                                        } else {
                                            handleStyleChange({ gridDirection: 'horizontal' });
                                        }
                                    }}
                                    title="Horizontal (Row)"
                                >
                                    <i className="fas fa-arrows-alt-h"></i>
                                    <span>Horizontal</span>
                                </button>
                                <button
                                    className={`grid-direction-btn ${
                                        (() => {
                                            if (device === 'mobile') {
                                                const mobileDir = gridElement?.gridDirectionMobile ?? (gridElement?.gridDirection || 'horizontal');
                                                return mobileDir === 'vertical';
                                            } else {
                                                const desktopDir = gridElement?.gridDirection || 'horizontal';
                                                return desktopDir === 'vertical';
                                            }
                                        })()
                                    ? 'active' : ''}`}
                                    onClick={() => {
                                        if (device === 'mobile') {
                                            handleStyleChange({ gridDirectionMobile: 'vertical' });
                                        } else {
                                            handleStyleChange({ gridDirection: 'vertical' });
                                        }
                                    }}
                                    title="Vertical (Column)"
                                >
                                    <i className="fas fa-arrows-alt-v"></i>
                                    <span>Vertical</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Grid Flow Mode */}
                        <div className="grid-control-group-full">
                            <label className="grid-control-label">
                                Grid Flow {device === 'mobile' ? '(📱 Mobile)' : '(💻 Desktop)'}
                            </label>
                            <div className="grid-flow-buttons">
                                <button
                                    className={`grid-flow-btn ${(() => {
                                        const flowMode = device === 'mobile'
                                            ? (gridElement?.gridFlowModeMobile ?? gridElement?.gridFlowMode ?? 'fixed')
                                            : (gridElement?.gridFlowMode ?? 'fixed');
                                        return flowMode === 'fixed';
                                    })() ? 'active' : ''}`}
                                    onClick={() => {
                                        if (device === 'mobile') {
                                            handleStyleChange({ gridFlowModeMobile: 'fixed' });
                                        } else {
                                            handleStyleChange({ gridFlowMode: 'fixed' });
                                        }
                                    }}
                                    title="Fixed Columns"
                                >
                                    <i className="fas fa-th"></i>
                                    <span>Fixed</span>
                                </button>
                                <button
                                    className={`grid-flow-btn ${(() => {
                                        const flowMode = device === 'mobile'
                                            ? (gridElement?.gridFlowModeMobile ?? gridElement?.gridFlowMode ?? 'fixed')
                                            : (gridElement?.gridFlowMode ?? 'fixed');
                                        return flowMode === 'auto-fit';
                                    })() ? 'active' : ''}`}
                                    onClick={() => {
                                        if (device === 'mobile') {
                                            handleStyleChange({ gridFlowModeMobile: 'auto-fit' });
                                        } else {
                                            handleStyleChange({ gridFlowMode: 'auto-fit' });
                                        }
                                    }}
                                    title="Auto-Fit (Responsive Wrap)"
                                >
                                    <i className="fas fa-expand-arrows-alt"></i>
                                    <span>Auto-Fit</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Max Items per Row (nur für auto-fit) - responsive */}
                        {(() => {
                            const flowMode = device === 'mobile'
                                ? (gridElement?.gridFlowModeMobile ?? gridElement?.gridFlowMode ?? 'fixed')
                                : (gridElement?.gridFlowMode ?? 'fixed');
                            const activeMaxItems = device === 'mobile'
                                ? (gridElement?.maxItemsPerRowMobile ?? gridElement?.maxItemsPerRow ?? 3)
                                : (gridElement?.maxItemsPerRow ?? 3);
                            
                            return flowMode === 'auto-fit' && (
                                <div className="grid-control-group">
                                    <label className="grid-control-label">Max per Row</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="6"
                                        step="1"
                                        value={activeMaxItems}
                                        onChange={(e) => {
                                            if (device === 'mobile') {
                                                handleStyleChange({ maxItemsPerRowMobile: parseInt(e.target.value) });
                                            } else {
                                                handleStyleChange({ maxItemsPerRow: parseInt(e.target.value) });
                                            }
                                        }}
                                        className="grid-slider"
                                    />
                                    <span className="grid-control-value">{activeMaxItems}</span>
                                </div>
                            );
                        })()}
                        
                        {/* Min Item Width (nur für auto-fit) - responsive */}
                        {(() => {
                            const flowMode = device === 'mobile'
                                ? (gridElement?.gridFlowModeMobile ?? gridElement?.gridFlowMode ?? 'fixed')
                                : (gridElement?.gridFlowMode ?? 'fixed');
                            const activeMinWidth = device === 'mobile'
                                ? (gridElement?.minItemWidthMobile ?? gridElement?.minItemWidth ?? 200)
                                : (gridElement?.minItemWidth ?? 200);
                            
                            return flowMode === 'auto-fit' && (
                                <div className="grid-control-group">
                                    <label className="grid-control-label">Min Width</label>
                                    <input
                                        type="range"
                                        min="100"
                                        max="500"
                                        step="20"
                                        value={activeMinWidth}
                                        onChange={(e) => {
                                            if (device === 'mobile') {
                                                handleStyleChange({ minItemWidthMobile: parseInt(e.target.value) });
                                            } else {
                                                handleStyleChange({ minItemWidth: parseInt(e.target.value) });
                                            }
                                        }}
                                        className="grid-slider"
                                    />
                                    <span className="grid-control-value">{activeMinWidth}px</span>
                                </div>
                            );
                        })()}
                        
                        {/* Gap */}
                        <div className="grid-control-group">
                            <label className="grid-control-label">Gap</label>
                            <input
                                type="range"
                                min="0"
                                max="300"
                                step="5"
                                value={activeGap}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (device === 'mobile') {
                                        handleStyleChange({ gapMobile: val });
                                    } else {
                                        handleStyleChange({ gap: val });
                                    }
                                }}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">{activeGap}px</span>
                        </div>
                        
                        {/* Column Template (nur für fixed mode) - responsive & direction-aware */}
                        {(() => {
                            const flowMode = device === 'mobile'
                                ? (gridElement?.gridFlowModeMobile ?? gridElement?.gridFlowMode ?? 'fixed')
                                : (gridElement?.gridFlowMode ?? 'fixed');
                            const activeTemplate = device === 'mobile'
                                ? (gridElement?.columnTemplateMobile ?? gridElement?.columnTemplate ?? 'equal')
                                : (gridElement?.columnTemplate ?? 'equal');
                            
                            // ✅ Dynamisches Label basierend auf Grid Direction
                            const currentDirection = device === 'mobile'
                                ? (gridElement?.gridDirectionMobile ?? gridElement?.gridDirection ?? 'horizontal')
                                : (gridElement?.gridDirection ?? 'horizontal');
                            
                            const templateLabel = currentDirection === 'horizontal' 
                                ? 'Column Template' 
                                : 'Row Template';
                            
                            return flowMode === 'fixed' && (
                                <>
                                    <div className="grid-section-divider"></div>
                                    <div className="grid-control-group-full">
                                        <label className="grid-control-label">
                                            {templateLabel}
                                            {activeTemplate && columnTemplates.find(t => t.value === activeTemplate) && (
                                                <span className="grid-control-sublabel">
                                                    ({columnTemplates.find(t => t.value === activeTemplate).preview})
                                                </span>
                                            )}
                                        </label>
                                        <div className="grid-template-buttons">
                                            {columnTemplates.map(template => (
                                                <button
                                                    key={template.value}
                                                    className={`grid-template-btn ${activeTemplate === template.value ? 'active' : ''} ${template.isVertical ? 'vertical-template' : ''}`}
                                                    onClick={() => {
                                                        if (device === 'mobile') {
                                                            onStyleChange({ columnTemplateMobile: template.value });
                                                        } else {
                                                            onStyleChange({ columnTemplate: template.value });
                                                        }
                                                    }}
                                                    title={`${template.label}: ${template.description}`}
                                                >
                                                    <span className="grid-template-icon">{template.icon}</span>
                                                    <span className="grid-template-label">{template.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* ✨ NEW: Column Min-Width Controls */}
                                    {activeTemplate !== 'equal' && activeTemplate !== 'auto' && currentDirection === 'horizontal' && (
                                        <>
                                            <div className="grid-section-divider"></div>
                                            <div className="grid-control-group-full">
                                                <label className="grid-control-label">
                                                    <i className="fas fa-ruler-horizontal" style={{ marginRight: '6px' }}></i>
                                                    Column Min-Widths
                                                </label>
                                                {(() => {
                                                    const columnCount = gridElement?.columns || 2;
                                                    const minWidths = device === 'mobile'
                                                        ? (gridElement?.columnMinWidthsMobile ?? gridElement?.columnMinWidths ?? Array(columnCount).fill(null))
                                                        : (gridElement?.columnMinWidths ?? Array(columnCount).fill(null));
                                                    
                                                    // Stelle sicher dass Array die richtige Länge hat
                                                    const paddedMinWidths = Array(columnCount).fill(null).map((_, i) => minWidths[i] ?? null);
                                                    
                                                    return (
                                                        <div className="grid-column-minwidths">
                                                            {paddedMinWidths.map((minWidth, colIndex) => {
                                                                const columnLabels = {
                                                                    0: activeTemplate.startsWith('1-') ? 'Small' : activeTemplate.startsWith('2-') ? 'Big' : activeTemplate.startsWith('3-') ? 'Big' : `Col ${colIndex + 1}`,
                                                                    1: activeTemplate === '1-2' ? 'Big' : activeTemplate === '2-1' ? 'Small' : activeTemplate === '1-3' ? 'Big' : activeTemplate === '3-1' ? 'Small' : activeTemplate === '1-2-1' ? 'Big' : activeTemplate === '2-1-1' ? 'Small' : `Col ${colIndex + 1}`,
                                                                    2: activeTemplate === '1-2-1' ? 'Small' : activeTemplate === '2-1-1' ? 'Small' : `Col ${colIndex + 1}`,
                                                                    3: `Col ${colIndex + 1}`
                                                                };
                                                                
                                                                return (
                                                                    <div key={colIndex} className="grid-column-minwidth-control">
                                                                        <label className="grid-column-minwidth-label">
                                                                            {columnLabels[colIndex]}
                                                                        </label>
                                                                        <div className="grid-column-minwidth-row">
                                                                            <input
                                                                                type="range"
                                                                                min="0"
                                                                                max="800"
                                                                                step="20"
                                                                                value={minWidth || 0}
                                                                                onChange={(e) => {
                                                                                    const newMinWidths = [...paddedMinWidths];
                                                                                    const val = parseInt(e.target.value);
                                                                                    newMinWidths[colIndex] = val === 0 ? null : val;
                                                                                    
                                                                                    if (device === 'mobile') {
                                                                                        handleStyleChange({ columnMinWidthsMobile: newMinWidths });
                                                                                    } else {
                                                                                        handleStyleChange({ columnMinWidths: newMinWidths });
                                                                                    }
                                                                                }}
                                                                                className="grid-slider"
                                                                            />
                                                                            <span className="grid-control-value" style={{ minWidth: '55px' }}>
                                                                                {minWidth ? `${minWidth}px` : 'Auto'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })()}
                                                <div className="grid-section-info" style={{ marginTop: '8px' }}>
                                                    <i className="fas fa-info-circle"></i>
                                                    <span>Set minimum width for each column. 0 = Auto (no minimum).</span>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </>
                            );
                        })()}
                        
                        <div className="grid-section-divider"></div>
                        
                        {/* ✅ Max Width Toggle - Full Width vs Limited Width */}
                        <div className="grid-control-group-full">
                            <label className="grid-control-label">Width Mode</label>
                            <div className="grid-direction-buttons">
                                <button
                                    className={`grid-direction-btn ${
                                        (() => {
                                            const currentMaxWidth = device === 'mobile'
                                                ? (gridElement?.maxWidthMobile ?? gridElement?.maxWidth ?? 1200)
                                                : (gridElement?.maxWidth ?? 1200);
                                            return currentMaxWidth >= 9000; // Full Width = 9999px
                                        })()
                                        ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                        if (device === 'mobile') {
                                            onStyleChange({ maxWidthMobile: 9999 }); // Full Width
                                        } else {
                                            onStyleChange({ maxWidth: 9999 }); // Full Width
                                        }
                                    }}
                                    title="Full Width"
                                >
                                    <i className="fas fa-arrows-alt-h"></i>
                                    <span>Full Width</span>
                                </button>
                                <button
                                    className={`grid-direction-btn ${
                                        (() => {
                                            const currentMaxWidth = device === 'mobile'
                                                ? (gridElement?.maxWidthMobile ?? gridElement?.maxWidth ?? 1200)
                                                : (gridElement?.maxWidth ?? 1200);
                                            return currentMaxWidth < 9000; // Limited Width
                                        })()
                                        ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                        if (device === 'mobile') {
                                            onStyleChange({ maxWidthMobile: 1200 }); // Default Limited Width
                                        } else {
                                            onStyleChange({ maxWidth: 1200 }); // Default Limited Width
                                        }
                                    }}
                                    title="Limited Width"
                                >
                                    <i className="fas fa-compress-alt"></i>
                                    <span>Limited</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* ✅ Max Width Slider - Nur bei Limited Width */}
                        {(() => {
                            const currentMaxWidth = device === 'mobile'
                                ? (gridElement?.maxWidthMobile ?? gridElement?.maxWidth ?? 1200)
                                : (gridElement?.maxWidth ?? 1200);
                            
                            return currentMaxWidth < 9000 && (
                                <div className="grid-control-group">
                                    <label className="grid-control-label">Max Width</label>
                                    <input
                                        type="range"
                                        min="300"
                                        max="1600"
                                        step="50"
                                        value={device === 'mobile'
                                            ? (gridElement?.maxWidthMobile ?? gridElement?.maxWidth ?? 1200)
                                            : (gridElement?.maxWidth ?? 1200)
                                        }
                                        onChange={(e) => {
                                            const newMaxWidth = parseInt(e.target.value);
                                            if (device === 'mobile') {
                                                handleStyleChange({ maxWidthMobile: newMaxWidth });
                                            } else {
                                                handleStyleChange({ maxWidth: newMaxWidth });
                                            }
                                        }}
                                        className="grid-slider"
                                    />
                                    <span className="grid-control-value">
                                        {device === 'mobile'
                                            ? (gridElement?.maxWidthMobile ?? gridElement?.maxWidth ?? 1200)
                                            : (gridElement?.maxWidth ?? 1200)
                                        }px
                                    </span>
                                </div>
                            );
                        })()}
                        
                        {/* ✅ Info Text für Max Width */}
                        <div className="grid-section-info">
                            <i className="fas fa-info-circle"></i>
                            <span>Max Width limits the grid width and centers it automatically.</span>
                        </div>
                    </div>
                )}
                
                {/* ✨ SPACING SECTION */}
                {expandedSection === 'spacing' && (
                    <div className="grid-expanded-section">
                        {/* Device Toggle */}
                        <div className="grid-device-toggle">
                            <button
                                className={device === 'desktop' ? 'active' : ''}
                                onClick={() => setDevice('desktop')}
                                title="Desktop"
                            >
                                <i className="fas fa-desktop"></i>
                                <span>Desktop</span>
                            </button>
                            <button
                                className={device === 'mobile' ? 'active' : ''}
                                onClick={() => setDevice('mobile')}
                                title="Mobile"
                            >
                                <i className="fas fa-mobile-alt"></i>
                                <span>Mobile</span>
                            </button>
                        </div>
                        
                        <div className="grid-control-group">
                            <label className="grid-control-label">Margin Top</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={activeMarginTop}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (device === 'mobile') {
                                        handleStyleChange({ marginTopMobile: val });
                                    } else {
                                        handleStyleChange({ marginTop: val });
                                    }
                                }}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">{activeMarginTop}px</span>
                        </div>
                        
                        <div className="grid-control-group">
                            <label className="grid-control-label">Margin Bottom</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={activeMarginBottom}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (device === 'mobile') {
                                        handleStyleChange({ marginBottomMobile: val });
                                    } else {
                                        handleStyleChange({ marginBottom: val });
                                    }
                                }}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">{activeMarginBottom}px</span>
                        </div>
                        
                        <div className="grid-control-group">
                            <label className="grid-control-label">Margin Left</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={activeMarginLeft}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (device === 'mobile') {
                                        handleStyleChange({ marginLeftMobile: val });
                                    } else {
                                        handleStyleChange({ marginLeft: val });
                                    }
                                }}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">{activeMarginLeft}px</span>
                        </div>
                        
                        <div className="grid-control-group">
                            <label className="grid-control-label">Margin Right</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={activeMarginRight}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (device === 'mobile') {
                                        handleStyleChange({ marginRightMobile: val });
                                    } else {
                                        handleStyleChange({ marginRight: val });
                                    }
                                }}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">{activeMarginRight}px</span>
                        </div>
                        
                        {/* ✨ DIVIDER */}
                        <div className="grid-section-divider"></div>
                        
                        {/* ✨ PADDING SLIDERS */}
                        <div className="grid-control-group">
                            <label className="grid-control-label">Padding Top</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={device === 'mobile'
                                    ? (gridElement?.paddingTopMobile ?? gridElement?.paddingTop ?? gridElement?.paddingMobile ?? gridElement?.padding ?? 0)
                                    : (gridElement?.paddingTop ?? gridElement?.padding ?? 0)
                                }
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (device === 'mobile') {
                                        handleStyleChange({ paddingTopMobile: val });
                                    } else {
                                        handleStyleChange({ paddingTop: val });
                                    }
                                }}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">
                                {device === 'mobile'
                                    ? (gridElement?.paddingTopMobile ?? gridElement?.paddingTop ?? gridElement?.paddingMobile ?? gridElement?.padding ?? 0)
                                    : (gridElement?.paddingTop ?? gridElement?.padding ?? 0)
                                }px
                            </span>
                        </div>
                        
                        <div className="grid-control-group">
                            <label className="grid-control-label">Padding Right</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={device === 'mobile'
                                    ? (gridElement?.paddingRightMobile ?? gridElement?.paddingRight ?? gridElement?.paddingMobile ?? gridElement?.padding ?? 0)
                                    : (gridElement?.paddingRight ?? gridElement?.padding ?? 0)
                                }
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (device === 'mobile') {
                                        handleStyleChange({ paddingRightMobile: val });
                                    } else {
                                        handleStyleChange({ paddingRight: val });
                                    }
                                }}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">
                                {device === 'mobile'
                                    ? (gridElement?.paddingRightMobile ?? gridElement?.paddingRight ?? gridElement?.paddingMobile ?? gridElement?.padding ?? 0)
                                    : (gridElement?.paddingRight ?? gridElement?.padding ?? 0)
                                }px
                            </span>
                        </div>
                        
                        <div className="grid-control-group">
                            <label className="grid-control-label">Padding Bottom</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={device === 'mobile'
                                    ? (gridElement?.paddingBottomMobile ?? gridElement?.paddingBottom ?? gridElement?.paddingMobile ?? gridElement?.padding ?? 0)
                                    : (gridElement?.paddingBottom ?? gridElement?.padding ?? 0)
                                }
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (device === 'mobile') {
                                        handleStyleChange({ paddingBottomMobile: val });
                                    } else {
                                        handleStyleChange({ paddingBottom: val });
                                    }
                                }}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">
                                {device === 'mobile'
                                    ? (gridElement?.paddingBottomMobile ?? gridElement?.paddingBottom ?? gridElement?.paddingMobile ?? gridElement?.padding ?? 0)
                                    : (gridElement?.paddingBottom ?? gridElement?.padding ?? 0)
                                }px
                            </span>
                        </div>
                        
                        <div className="grid-control-group">
                            <label className="grid-control-label">Padding Left</label>
                            <input
                                type="range"
                                min="0"
                                max="200"
                                step="5"
                                value={device === 'mobile'
                                    ? (gridElement?.paddingLeftMobile ?? gridElement?.paddingLeft ?? gridElement?.paddingMobile ?? gridElement?.padding ?? 0)
                                    : (gridElement?.paddingLeft ?? gridElement?.padding ?? 0)
                                }
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (device === 'mobile') {
                                        handleStyleChange({ paddingLeftMobile: val });
                                    } else {
                                        handleStyleChange({ paddingLeft: val });
                                    }
                                }}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">
                                {device === 'mobile'
                                    ? (gridElement?.paddingLeftMobile ?? gridElement?.paddingLeft ?? gridElement?.paddingMobile ?? gridElement?.padding ?? 0)
                                    : (gridElement?.paddingLeft ?? gridElement?.padding ?? 0)
                                }px
                            </span>
                        </div>
                    </div>
                )}
                
                {/* ✨ BORDER RADIUS SECTION */}
                {expandedSection === 'borderRadius' && (
                    <div className="grid-expanded-section">
                        <div className="grid-control-group">
                            <label className="grid-control-label">Radius</label>
                            <input
                                type="range"
                                min="0"
                                max="50"
                                value={gridElement?.borderRadius || 0}
                                onChange={(e) => onStyleChange({ borderRadius: parseInt(e.target.value) })}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">{gridElement?.borderRadius || 0}px</span>
                        </div>
                    </div>
                )}
                
                {/* ✨ BORDER SECTION */}
                {expandedSection === 'border' && (
                    <div className="grid-expanded-section">
                        <label className="grid-toggle-label">
                            <input
                                type="checkbox"
                                checked={gridElement?.borderEnabled || false}
                                onChange={(e) => onStyleChange({ borderEnabled: e.target.checked })}
                                className="grid-toggle-checkbox"
                            />
                            <span className="grid-toggle-text">Enable Border</span>
                        </label>
                        
                        {/* ✨ SEAMLESS MODE TOGGLE - No double borders! */}
                        <label className="grid-toggle-label" style={{ marginTop: '12px' }}>
                            <input
                                type="checkbox"
                                checked={gridElement?.seamlessMode || false}
                                onChange={(e) => {
                                    const isSeamless = e.target.checked;
                                    if (isSeamless) {
                                        // ✅ Seamless aktivieren → Gap = 0, Border enabled
                                        onStyleChange({ 
                                            seamlessMode: true,
                                            gap: 0,
                                            gapMobile: 0,
                                            borderEnabled: true,
                                            seamlessBorderWidth: gridElement?.seamlessBorderWidth || 1,
                                            seamlessBorderColor: gridElement?.seamlessBorderColor || '#e0e0e0',
                                            seamlessBorderStyle: gridElement?.seamlessBorderStyle || 'solid'
                                        });
                                    } else {
                                        // ✅ Seamless deaktivieren → Gap wiederherstellen
                                        onStyleChange({ 
                                            seamlessMode: false,
                                            gap: 20,
                                            gapMobile: 15
                                        });
                                    }
                                }}
                                className="grid-toggle-checkbox"
                            />
                            <span className="grid-toggle-text">
                                <i className="fas fa-border-all" style={{ marginRight: '6px' }}></i>
                                Seamless Mode (No Double Borders)
                            </span>
                        </label>
                        
                        {/* ✨ Seamless Mode Info */}
                        {gridElement?.seamlessMode && (
                            <div className="grid-info-box" style={{ marginTop: '8px', padding: '10px', background: 'rgba(102, 126, 234, 0.1)', borderRadius: '6px', fontSize: '12px', color: '#667eea' }}>
                                <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                                Seamless Mode: Cells are separated by divider lines without double borders
                            </div>
                        )}
                        
                        {/* ✨ Seamless Border Settings */}
                        {gridElement?.seamlessMode && (
                            <>
                                <div className="grid-control-group" style={{ marginTop: '12px' }}>
                                    <label className="grid-control-label">Divider Width</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="5"
                                        value={gridElement?.seamlessBorderWidth || 1}
                                        onChange={(e) => onStyleChange({ seamlessBorderWidth: parseInt(e.target.value) })}
                                        className="grid-slider"
                                    />
                                    <span className="grid-control-value">{gridElement?.seamlessBorderWidth || 1}px</span>
                                </div>
                                
                                <div className="grid-control-group">
                                    <label className="grid-control-label">Divider Color</label>
                                    <input
                                        type="color"
                                        value={gridElement?.seamlessBorderColor || '#e0e0e0'}
                                        onChange={(e) => onStyleChange({ seamlessBorderColor: e.target.value })}
                                        className="grid-color-picker-compact"
                                    />
                                </div>
                                
                                <div className="grid-control-group">
                                    <label className="grid-control-label">Divider Style</label>
                                    <select
                                        value={gridElement?.seamlessBorderStyle || 'solid'}
                                        onChange={(e) => onStyleChange({ seamlessBorderStyle: e.target.value })}
                                        className="grid-select-compact"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            color: 'white',
                                            fontSize: '13px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="solid" style={{ background: '#2a2a3e', color: 'white' }}>Solid</option>
                                        <option value="dashed" style={{ background: '#2a2a3e', color: 'white' }}>Dashed</option>
                                        <option value="dotted" style={{ background: '#2a2a3e', color: 'white' }}>Dotted</option>
                                    </select>
                                </div>
                            </>
                        )}
                        
                        {gridElement?.borderEnabled && !gridElement?.seamlessMode && (
                            <>
                                <div className="grid-control-group">
                                    <label className="grid-control-label">Width</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={gridElement?.borderWidth || 1}
                                        onChange={(e) => onStyleChange({ borderWidth: parseInt(e.target.value) })}
                                        className="grid-slider"
                                    />
                                    <span className="grid-control-value">{gridElement?.borderWidth || 1}px</span>
                                </div>
                                
                                <div className="grid-control-group">
                                    <label className="grid-control-label">Color</label>
                                    <input
                                        type="color"
                                        value={gridElement?.borderColor || '#e0e0e0'}
                                        onChange={(e) => onStyleChange({ borderColor: e.target.value })}
                                        className="grid-color-picker-compact"
                                    />
                                </div>
                            </>
                        )}
                        
                        {/* ✨ Grid Box Shadow */}
                        <div className="grid-effect-group" style={{ marginTop: '16px' }}>
                            <label className="grid-effect-label">
                                <i className="fas fa-layer-group" style={{ marginRight: '6px' }}></i>
                                Grid Shadow
                            </label>
                            <div className="grid-effect-presets">
                                <button
                                    className={`grid-preset-btn ${!gridElement?.boxShadow || gridElement?.boxShadow === 'none' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ boxShadow: 'none' })}
                                    title="No Shadow"
                                >
                                    <i className="fas fa-times"></i>
                                    <span>None</span>
                                </button>
                                <button
                                    className={`grid-preset-btn ${gridElement?.boxShadow === 'small' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ boxShadow: 'small' })}
                                    title="Small Shadow"
                                >
                                    <div className="grid-shadow-preview small"></div>
                                    <span>Small</span>
                                </button>
                                <button
                                    className={`grid-preset-btn ${gridElement?.boxShadow === 'medium' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ boxShadow: 'medium' })}
                                    title="Medium Shadow"
                                >
                                    <div className="grid-shadow-preview medium"></div>
                                    <span>Medium</span>
                                </button>
                                <button
                                    className={`grid-preset-btn ${gridElement?.boxShadow === 'large' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ boxShadow: 'large' })}
                                    title="Large Shadow"
                                >
                                    <div className="grid-shadow-preview large"></div>
                                    <span>Large</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* ✨ CELL LAYOUT SECTION - Eigene Komponente mit Alignment */}
                {expandedSection === 'cellLayout' && (
                    <CellLayoutSettings
                        gridElement={gridElement}
                        onStyleChange={onStyleChange}
                        device={device}
                        gridFlowMode={device === 'mobile' 
                            ? (gridElement?.gridFlowModeMobile ?? gridElement?.gridFlowMode ?? 'fixed')
                            : (gridElement?.gridFlowMode ?? 'fixed')
                        }
                    />
                )}
                
                {/* ✨ BACKGROUND SECTION - COMPACT wie Button Toolbar */}
                {expandedSection === 'background' && (
                    <div className="grid-expanded-section">
                        <div className="grid-color-group">
                            <span className="grid-color-label-compact">Grid Background</span>
                            <div className="grid-colors-compact">
                                {/* Transparent */}
                                <button
                                    className={`grid-color-btn-compact transparent ${gridElement?.backgroundColor === 'transparent' || !gridElement?.backgroundColor ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ backgroundColor: 'transparent' })}
                                    title="Transparent"
                                >
                                    <div className="grid-transparent-icon"></div>
                                </button>
                                
                                {/* Brand Colors */}
                                {Object.values(colors).slice(0, 5).map((color, index) => (
                                    <button
                                        key={index}
                                        className={`grid-color-btn-compact ${gridElement?.backgroundColor === color ? 'active' : ''}`}
                                        style={{ background: color }}
                                        onClick={() => onStyleChange({ backgroundColor: color })}
                                        title={`Color ${index + 1}`}
                                    />
                                ))}
                                
                                {/* Custom Color Slots */}
                                {customColorSlots.map((slotColor, slotIndex) => {
                                    const isEmpty = slotColor === '#ffffff';
                                    const isActive = gridElement?.backgroundColor === slotColor && !isEmpty;
                                    
                                    return (
                                        <button
                                            key={slotIndex}
                                            className={`grid-color-btn-compact ${isEmpty ? 'empty' : ''} ${isActive ? 'active' : ''}`}
                                            style={{ background: isEmpty ? 'transparent' : slotColor }}
                                            onClick={() => {
                                                if (isEmpty) {
                                                    const input = document.getElementById(`grid-bg-slot-${slotIndex}`);
                                                    if (input) input.click();
                                                } else {
                                                    onStyleChange({ backgroundColor: slotColor });
                                                }
                                            }}
                                            onDoubleClick={() => {
                                                if (!isEmpty) {
                                                    const input = document.getElementById(`grid-bg-slot-${slotIndex}`);
                                                    if (input) input.click();
                                                }
                                            }}
                                            title={isEmpty ? 'Add custom' : `Custom ${slotIndex + 1}`}
                                        >
                                            {isEmpty && <i className="fas fa-plus"></i>}
                                            <input
                                                id={`grid-bg-slot-${slotIndex}`}
                                                type="color"
                                                value={slotColor}
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    const newSlots = [...customColorSlots];
                                                    newSlots[slotIndex] = e.target.value;
                                                    setCustomColorSlots(newSlots);
                                                    onStyleChange({ backgroundColor: e.target.value });
                                                }}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
                
                {/* ✨ CELL STYLING SECTION - Default Styles for ALL Cells */}
                {expandedSection === 'cellStyling' && (
                    <div className="grid-expanded-section">
                        {/* 🎨 CELL BACKGROUND - wie Button Toolbar */}
                        <div className="grid-color-group">
                            <div className="grid-effect-header" style={{ 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                marginBottom: '16px',
                                gap: '12px'
                            }}>
                                <span className="grid-color-label-compact" style={{ 
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    textAlign: 'center'
                                }}>Cell Background</span>
                                {/* ✅ Gradient Toggle wie beim Button */}
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={gridElement?.cellDefaultGradientEnabled || false}
                                        onChange={(e) => {
                                            const isEnabled = e.target.checked;
                                            
                                            // ✅ FIX: Wenn Gradient deaktiviert wird, müssen wir die individuellen Cell gradientEnabled zurücksetzen
                                            if (!isEnabled && gridElement?.children) {
                                                // Reset gradientEnabled für alle Cells
                                                const updatedChildren = gridElement.children.map(cell => {
                                                    const { gradientEnabled, ...cellWithoutGradient } = cell;
                                                    return cellWithoutGradient; // ✅ Remove gradientEnabled property completely (no undefined!)
                                                });
                                                
                                                onStyleChange({ 
                                                    cellDefaultGradientEnabled: false,
                                                    children: updatedChildren
                                                });
                                            } else {
                                                // Gradient aktivieren - KEINE children überschreiben (Cells behalten ihre Settings)
                                                onStyleChange({ 
                                                    cellDefaultGradientEnabled: isEnabled
                                                });
                                            }
                                        }}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Gradient</span>
                                </label>
                            </div>
                            
                            {/* Normal Background Colors (Brand Colors + Custom Slots, kein Gradient) */}
                            {!gridElement?.cellDefaultGradientEnabled && (
                                <>
                                    {/* Brand Colors Row */}
                                    <div className="button-brand-colors">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`button-brand-color ${gridElement?.cellDefaultBackground === brandColor ? 'active' : ''}`}
                                                style={{ backgroundColor: brandColor }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        
                                                        // ✅ FIX: Wenn Solid Color gewählt wird, Gradient für alle Cells deaktivieren
                                                        if (gridElement?.children) {
                                                            const updatedChildren = gridElement.children.map(cell => {
                                                                // ✅ Remove gradientEnabled & backgroundColor properties (no undefined!)
                                                                const { gradientEnabled, backgroundColor, ...cellCleaned } = cell;
                                                                return cellCleaned;
                                                            });
                                                            
                                                            onStyleChange({ 
                                                                cellDefaultBackground: brandColor,
                                                                cellDefaultGradientEnabled: false, // ✅ Disable gradient mode
                                                                children: updatedChildren
                                                            });
                                                        } else {
                                                            onStyleChange({ 
                                                                cellDefaultBackground: brandColor,
                                                                cellDefaultGradientEnabled: false
                                                            });
                                                        }
                                                    }}
                                                title={`Color ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                    
                                    {/* Custom Color Slots Row */}
                                    <div className="button-brand-colors" style={{ marginTop: '8px' }}>
                                        {customColorSlots.map((slotColor, slotIndex) => {
                                            const isEmpty = slotColor === '#ffffff';
                                            const isActive = gridElement?.cellDefaultBackground === slotColor && !isEmpty;
                                            
                                            return (
                                                <button
                                                    key={slotIndex}
                                                    type="button"
                                                    className={`button-brand-color ${isEmpty ? 'empty' : ''} ${isActive ? 'active' : ''}`}
                                                    style={{ 
                                                        backgroundColor: isEmpty ? 'transparent' : slotColor,
                                                        border: isEmpty ? '2px dashed rgba(255, 255, 255, 0.2)' : '2px solid rgba(255, 255, 255, 0.3)'
                                                    }}
                                                    onClick={() => {
                                                        if (isEmpty) {
                                                            const input = document.getElementById(`grid-cell-bg-slot-${slotIndex}`);
                                                            if (input) input.click();
                                                        } else {
                                                            // ✅ FIX: Wenn Solid Color gewählt wird, Gradient für alle Cells deaktivieren
                                                            if (gridElement?.children) {
                                                                const updatedChildren = gridElement.children.map(cell => {
                                                                    const { gradientEnabled, backgroundColor, ...cellCleaned } = cell;
                                                                    return cellCleaned;
                                                                });
                                                                
                                                                onStyleChange({ 
                                                                    cellDefaultBackground: slotColor,
                                                                    cellDefaultGradientEnabled: false,
                                                                    children: updatedChildren
                                                                });
                                                            } else {
                                                                onStyleChange({ 
                                                                    cellDefaultBackground: slotColor,
                                                                    cellDefaultGradientEnabled: false
                                                                });
                                                            }
                                                        }
                                                    }}
                                                    onDoubleClick={() => {
                                                        const input = document.getElementById(`grid-cell-bg-slot-${slotIndex}`);
                                                        if (input) input.click();
                                                    }}
                                                    title={isEmpty ? 'Add custom background color' : `Custom BG ${slotIndex + 1}`}
                                                >
                                                    {isEmpty && <i className="fas fa-plus"></i>}
                                                    <input
                                                        id={`grid-cell-bg-slot-${slotIndex}`}
                                                        type="color"
                                                        value={slotColor}
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => {
                                                            const newSlots = [...customColorSlots];
                                                            newSlots[slotIndex] = e.target.value;
                                                            setCustomColorSlots(newSlots);
                                                            
                                                            // ✅ FIX: Wenn Solid Color gewählt wird, Gradient für alle Cells deaktivieren
                                                            if (gridElement?.children) {
                                                                const updatedChildren = gridElement.children.map(cell => {
                                                                    const { gradientEnabled, backgroundColor, ...cellCleaned } = cell;
                                                                    return cellCleaned;
                                                                });
                                                                
                                                                onStyleChange({ 
                                                                    cellDefaultBackground: e.target.value,
                                                                    cellDefaultGradientEnabled: false,
                                                                    children: updatedChildren
                                                                });
                                                            } else {
                                                                onStyleChange({ 
                                                                    cellDefaultBackground: e.target.value,
                                                                    cellDefaultGradientEnabled: false
                                                                });
                                                            }
                                                        }}
                                                    />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                            
                            {/* Gradient Colors (wenn Gradient aktiviert) */}
                            {gridElement?.cellDefaultGradientEnabled && (
                                <div className="button-gradient-controls">
                                    {/* Color 1 */}
                                    <div className="button-gradient-row">
                                        <span className="button-gradient-label">Color 1</span>
                                        <div className="button-brand-colors button-brand-colors-small">
                                            {/* Brand Colors */}
                                            {Object.values(colors).map((brandColor, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    className={`button-brand-color button-brand-color-small ${gridElement?.cellDefaultGradientColor1 === brandColor ? 'active' : ''}`}
                                                    style={{ backgroundColor: brandColor }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        
                                                        // ✅ FIX: Wenn Gradient Color gewählt wird, Gradient automatisch aktivieren
                                                        onStyleChange({ 
                                                            cellDefaultGradientColor1: brandColor,
                                                            cellDefaultGradientEnabled: true // ✅ Activate gradient
                                                        });
                                                    }}
                                                    title={`Gradient Color 1`}
                                                />
                                            ))}
                                            
                                            {/* Custom Color Slots */}
                                            {customColorSlots.map((slotColor, slotIndex) => {
                                                const isEmpty = slotColor === '#ffffff';
                                                const isActive = gridElement?.cellDefaultGradientColor1 === slotColor && !isEmpty;
                                                
                                                return (
                                                    <button
                                                        key={`grad1-${slotIndex}`}
                                                        type="button"
                                                        className={`button-brand-color button-brand-color-small ${isEmpty ? 'empty' : ''} ${isActive ? 'active' : ''}`}
                                                        style={{ 
                                                            backgroundColor: isEmpty ? 'transparent' : slotColor,
                                                            border: isEmpty ? '2px dashed rgba(255, 255, 255, 0.2)' : '2px solid rgba(255, 255, 255, 0.3)'
                                                        }}
                                                        onClick={() => {
                                                            if (isEmpty) {
                                                                const input = document.getElementById(`grid-grad1-slot-${slotIndex}`);
                                                                if (input) input.click();
                                                            } else {
                                                                // ✅ FIX: Wenn Gradient Color gewählt wird, Gradient automatisch aktivieren
                                                                onStyleChange({ 
                                                                    cellDefaultGradientColor1: slotColor,
                                                                    cellDefaultGradientEnabled: true
                                                                });
                                                            }
                                                        }}
                                                        onDoubleClick={() => {
                                                            const input = document.getElementById(`grid-grad1-slot-${slotIndex}`);
                                                            if (input) input.click();
                                                        }}
                                                        title={isEmpty ? 'Add custom gradient color' : `Custom Grad 1 ${slotIndex + 1}`}
                                                    >
                                                        {isEmpty && <i className="fas fa-plus"></i>}
                                                        <input
                                                            id={`grid-grad1-slot-${slotIndex}`}
                                                            type="color"
                                                            value={slotColor}
                                                            style={{ display: 'none' }}
                                                            onChange={(e) => {
                                                                const newSlots = [...customColorSlots];
                                                                newSlots[slotIndex] = e.target.value;
                                                                setCustomColorSlots(newSlots);
                                                                
                                                                // ✅ FIX: Wenn Gradient Color gewählt wird, Gradient automatisch aktivieren
                                                                onStyleChange({ 
                                                                    cellDefaultGradientColor1: e.target.value,
                                                                    cellDefaultGradientEnabled: true
                                                                });
                                                            }}
                                                        />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    
                                    {/* Color 2 */}
                                    <div className="button-gradient-row">
                                        <span className="button-gradient-label">Color 2</span>
                                        <div className="button-brand-colors button-brand-colors-small">
                                            {/* Brand Colors */}
                                            {Object.values(colors).map((brandColor, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    className={`button-brand-color button-brand-color-small ${gridElement?.cellDefaultGradientColor2 === brandColor ? 'active' : ''}`}
                                                    style={{ backgroundColor: brandColor }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        
                                                        // ✅ FIX: Wenn Gradient Color gewählt wird, Gradient automatisch aktivieren
                                                        onStyleChange({ 
                                                            cellDefaultGradientColor2: brandColor,
                                                            cellDefaultGradientEnabled: true
                                                        });
                                                    }}
                                                    title={`Gradient Color 2`}
                                                />
                                            ))}
                                            
                                            {/* Custom Color Slots */}
                                            {customColorSlots.map((slotColor, slotIndex) => {
                                                const isEmpty = slotColor === '#ffffff';
                                                const isActive = gridElement?.cellDefaultGradientColor2 === slotColor && !isEmpty;
                                                
                                                return (
                                                    <button
                                                        key={`grad2-${slotIndex}`}
                                                        type="button"
                                                        className={`button-brand-color button-brand-color-small ${isEmpty ? 'empty' : ''} ${isActive ? 'active' : ''}`}
                                                        style={{ 
                                                            backgroundColor: isEmpty ? 'transparent' : slotColor,
                                                            border: isEmpty ? '2px dashed rgba(255, 255, 255, 0.2)' : '2px solid rgba(255, 255, 255, 0.3)'
                                                        }}
                                                        onClick={() => {
                                                            if (isEmpty) {
                                                                const input = document.getElementById(`grid-grad2-slot-${slotIndex}`);
                                                                if (input) input.click();
                                                            } else {
                                                                // ✅ FIX: Wenn Gradient Color gewählt wird, Gradient automatisch aktivieren
                                                                onStyleChange({ 
                                                                    cellDefaultGradientColor2: slotColor,
                                                                    cellDefaultGradientEnabled: true
                                                                });
                                                            }
                                                        }}
                                                        onDoubleClick={() => {
                                                            const input = document.getElementById(`grid-grad2-slot-${slotIndex}`);
                                                            if (input) input.click();
                                                        }}
                                                        title={isEmpty ? 'Add custom gradient color' : `Custom Grad 2 ${slotIndex + 1}`}
                                                    >
                                                        {isEmpty && <i className="fas fa-plus"></i>}
                                                        <input
                                                            id={`grid-grad2-slot-${slotIndex}`}
                                                            type="color"
                                                            value={slotColor}
                                                            style={{ display: 'none' }}
                                                            onChange={(e) => {
                                                                const newSlots = [...customColorSlots];
                                                                newSlots[slotIndex] = e.target.value;
                                                                setCustomColorSlots(newSlots);
                                                                
                                                                // ✅ FIX: Wenn Gradient Color gewählt wird, Gradient automatisch aktivieren
                                                                onStyleChange({ 
                                                                    cellDefaultGradientColor2: e.target.value,
                                                                    cellDefaultGradientEnabled: true
                                                                });
                                                            }}
                                                        />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Cell Background Opacity */}
                        <div className="grid-control-group" style={{ marginTop: '16px' }}>
                            <label className="grid-control-label">
                                <i className="fas fa-adjust" style={{ marginRight: '6px' }}></i>
                                Opacity
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                step="5"
                                value={gridElement?.cellDefaultOpacity ?? 100}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    handleStyleChange({ cellDefaultOpacity: val });
                                }}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">{gridElement?.cellDefaultOpacity ?? 100}%</span>
                        </div>
                        
                        {/* Cell Border Radius */}
                        <div className="grid-control-group" style={{ marginTop: '16px' }}>
                            <label className="grid-control-label">
                                <i className="fas fa-border-style" style={{ marginRight: '6px' }}></i>
                                Border Radius
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="50"
                                step="2"
                                value={gridElement?.cellDefaultBorderRadius ?? 8}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    handleStyleChange({ cellDefaultBorderRadius: val });
                                }}
                                className="grid-slider"
                            />
                            <span className="grid-control-value">{gridElement?.cellDefaultBorderRadius ?? 8}px</span>
                        </div>
                        
                        {/* Cell Border Toggle & Settings */}
                        <label className="btn-custom-toggle-wrapper" style={{ marginTop: '20px' }}>
                            <input
                                type="checkbox"
                                checked={gridElement?.cellDefaultBorderEnabled || false}
                                onChange={(e) => onStyleChange({ cellDefaultBorderEnabled: e.target.checked })}
                                className="btn-custom-toggle-input"
                            />
                            <span className="btn-custom-toggle-slider"></span>
                            <span className="btn-custom-toggle-label">Cell Border</span>
                        </label>
                        
                        {gridElement?.cellDefaultBorderEnabled && (
                            <>
                                <div className="grid-control-group" style={{ marginTop: '16px' }}>
                                    <label className="grid-control-label">Border Width</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="8"
                                        step="1"
                                        value={gridElement?.cellDefaultBorderWidth ?? 1}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value);
                                            handleStyleChange({ cellDefaultBorderWidth: val });
                                        }}
                                        className="grid-slider"
                                    />
                                    <span className="grid-control-value">{gridElement?.cellDefaultBorderWidth ?? 1}px</span>
                                </div>
                                
                                {/* Border Color - Brand Colors + Custom Slots */}
                                <div className="grid-color-group" style={{ marginTop: '12px' }}>
                                    <span className="grid-color-label-compact">Border Color</span>
                                    <div className="button-brand-colors">
                                        {/* Brand Colors */}
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`button-brand-color ${gridElement?.cellDefaultBorderColor === brandColor ? 'active' : ''}`}
                                                style={{ backgroundColor: brandColor }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    
                                                    // ✅ FIXED: Nur die Default-Farbe ändern, KEINE children überschreiben!
                                                    onStyleChange({ 
                                                        cellDefaultBorderColor: brandColor
                                                    });
                                                }}
                                                title={`Border Color ${index + 1}`}
                                            />
                                        ))}
                                        
                                        {/* Custom Color Slots */}
                                        {customColorSlots.map((slotColor, slotIndex) => {
                                            const isEmpty = slotColor === '#ffffff';
                                            const isActive = gridElement?.cellDefaultBorderColor === slotColor && !isEmpty;
                                            
                                            return (
                                                <button
                                                    key={slotIndex}
                                                    type="button"
                                                    className={`button-brand-color ${isEmpty ? 'empty' : ''} ${isActive ? 'active' : ''}`}
                                                    style={{ 
                                                        backgroundColor: isEmpty ? 'transparent' : slotColor,
                                                        border: isEmpty ? '2px dashed rgba(255, 255, 255, 0.2)' : '2px solid rgba(255, 255, 255, 0.3)'
                                                    }}
                                                    onClick={() => {
                                                        if (isEmpty) {
                                                            const input = document.getElementById(`grid-border-color-slot-${slotIndex}`);
                                                            if (input) input.click();
                                                        } else {
                                                            // ✅ FIXED: Nur die Default-Farbe ändern, KEINE children überschreiben!
                                                            onStyleChange({ 
                                                                cellDefaultBorderColor: slotColor
                                                            });
                                                        }
                                                    }}
                                                    onDoubleClick={() => {
                                                        const input = document.getElementById(`grid-border-color-slot-${slotIndex}`);
                                                        if (input) input.click();
                                                    }}
                                                    title={isEmpty ? 'Add custom border color' : `Custom Border ${slotIndex + 1}`}
                                                >
                                                    {isEmpty && <i className="fas fa-plus"></i>}
                                                    <input
                                                        id={`grid-border-color-slot-${slotIndex}`}
                                                        type="color"
                                                        value={slotColor}
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => {
                                                            const newSlots = [...customColorSlots];
                                                            newSlots[slotIndex] = e.target.value;
                                                            setCustomColorSlots(newSlots);
                                                            
                                                            // ✅ FIXED: Nur die Default-Farbe ändern, KEINE children überschreiben!
                                                            onStyleChange({ 
                                                                cellDefaultBorderColor: e.target.value
                                                            });
                                                        }}
                                                    />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {/* Fixed Height Toggle */}
                        <label className="btn-custom-toggle-wrapper" style={{ marginTop: '20px' }}>
                            <input
                                type="checkbox"
                                checked={gridElement?.cellDefaultFixedHeight || false}
                                onChange={(e) => onStyleChange({ cellDefaultFixedHeight: e.target.checked })}
                                className="btn-custom-toggle-input"
                            />
                            <span className="btn-custom-toggle-slider"></span>
                            <span className="btn-custom-toggle-label">Fixed Cell Height</span>
                        </label>
                        
                        {gridElement?.cellDefaultFixedHeight && (
                            <div className="grid-control-group" style={{ marginTop: '16px' }}>
                                <label className="grid-control-label">Cell Height</label>
                                <input
                                    type="range"
                                    min="100"
                                    max="800"
                                    step="20"
                                    value={gridElement?.cellDefaultHeight ?? 200}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value);
                                        handleStyleChange({ cellDefaultHeight: val });
                                    }}
                                    className="grid-slider"
                                />
                                <span className="grid-control-value">{gridElement?.cellDefaultHeight ?? 200}px</span>
                            </div>
                        )}
                        
                        {/* Backdrop Blur (Glass Effect) */}
                        <div className="grid-effect-group" style={{ marginTop: '20px' }}>
                            <label className="grid-effect-label">
                                <i className="fas fa-circle-notch" style={{ marginRight: '6px' }}></i>
                                Backdrop Blur (Glass Effect)
                            </label>
                            <div className="grid-effect-presets">
                                <button
                                    className={`grid-preset-btn ${!gridElement?.cellDefaultBackdropBlur || gridElement?.cellDefaultBackdropBlur === 'none' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultBackdropBlur: 'none' })}
                                    title="No Blur"
                                >
                                    <i className="fas fa-times"></i>
                                    <span>None</span>
                                </button>
                                <button
                                    className={`grid-preset-btn ${gridElement?.cellDefaultBackdropBlur === 'light' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultBackdropBlur: 'light' })}
                                    title="Light Blur (4px)"
                                >
                                    <i className="fas fa-adjust"></i>
                                    <span>Light</span>
                                </button>
                                <button
                                    className={`grid-preset-btn ${gridElement?.cellDefaultBackdropBlur === 'medium' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultBackdropBlur: 'medium' })}
                                    title="Medium Blur (8px)"
                                >
                                    <i className="fas fa-circle"></i>
                                    <span>Medium</span>
                                </button>
                                <button
                                    className={`grid-preset-btn ${gridElement?.cellDefaultBackdropBlur === 'strong' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultBackdropBlur: 'strong' })}
                                    title="Strong Blur (16px)"
                                >
                                    <i className="fas fa-circle"></i>
                                    <span>Strong</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Box Shadow */}
                        <div className="grid-effect-group" style={{ marginTop: '16px' }}>
                            <label className="grid-effect-label">
                                <i className="fas fa-layer-group" style={{ marginRight: '6px' }}></i>
                                Box Shadow
                            </label>
                            <div className="grid-effect-presets">
                                <button
                                    className={`grid-preset-btn ${!gridElement?.cellDefaultBoxShadow || gridElement?.cellDefaultBoxShadow === 'none' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultBoxShadow: 'none' })}
                                    title="No Shadow"
                                >
                                    <i className="fas fa-times"></i>
                                    <span>None</span>
                                </button>
                                <button
                                    className={`grid-preset-btn ${gridElement?.cellDefaultBoxShadow === 'small' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultBoxShadow: 'small' })}
                                    title="Small Shadow"
                                >
                                    <div className="grid-shadow-preview small"></div>
                                    <span>Small</span>
                                </button>
                                <button
                                    className={`grid-preset-btn ${gridElement?.cellDefaultBoxShadow === 'medium' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultBoxShadow: 'medium' })}
                                    title="Medium Shadow"
                                >
                                    <div className="grid-shadow-preview medium"></div>
                                    <span>Medium</span>
                                </button>
                                <button
                                    className={`grid-preset-btn ${gridElement?.cellDefaultBoxShadow === 'large' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ cellDefaultBoxShadow: 'large' })}
                                    title="Large Shadow"
                                >
                                    <div className="grid-shadow-preview large"></div>
                                    <span>Large</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* ✅ Override Nested Backgrounds Toggle */}
                        <div className="grid-control-group-full" style={{ marginTop: '20px' }}>
                            <div className="grid-toggle-control">
                                <label className="grid-toggle-label">
                                    <i className="fas fa-layer-group" style={{ marginRight: '8px' }}></i>
                                    Override Nested Backgrounds
                                    <span style={{ fontSize: '11px', opacity: 0.7, marginLeft: '6px' }}>
                                        (Apply cell color to TextBoxes)
                                    </span>
                                </label>
                                <button
                                    className={`grid-toggle-btn ${(gridElement?.cellDefaultOverrideNestedBackgrounds !== false) ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ 
                                        cellDefaultOverrideNestedBackgrounds: (gridElement?.cellDefaultOverrideNestedBackgrounds === false) ? true : false
                                    })}
                                    title={(gridElement?.cellDefaultOverrideNestedBackgrounds !== false) ? 'Nested elements use cell color (default: ON)' : 'Nested elements keep own colors'}
                                >
                                    <i className={`fas fa-toggle-${(gridElement?.cellDefaultOverrideNestedBackgrounds !== false) ? 'on' : 'off'}`}></i>
                                </button>
                            </div>
                        </div>
                        
                        <span className="button-section-label" style={{ marginTop: '20px' }}>Cell Defaults</span>
                    </div>
                )}
            </div>
            </div>
        </div>
    );
    
    return ReactDOM.createPortal(toolbarContent, document.body);
}

export default GridFloatingToolbar;

